import React, { FC, useState, useEffect } from 'react';
import clsx from 'clsx';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Button, InlineMessages, InlineMessagesType, Types } from '@marriott/mi-ui-library';
import { AddressType } from '@marriott/mi-groups-graphql';
import { UserInfo, AddressInfo, AdditionalInfo, EventInfo, Attachments } from '../../molecules';
import { OptionType } from '../../molecules/EventInfo/EventInfo.types';
import { useCustomerInfo } from '../../hooks';
import {
  createCustomXRequestId,
  getSession,
  prepopulateSearchCriteria,
  getSubmitIntlRfpInput,
  getFormattedCalendarDate,
  getQueryParams,
  updateDataLayerWithRFPAttributes,
} from '../../utils';
import { useLocaleStore, useHeaderFieldsStore, useRfpStore, useRfpTrayStore } from '../../store';
import { CustomDomContentLoaded } from '../../components/CustomDomContentLoaded/CustomDomContentLoaded';
import {
  StyledIntlRfpEventInfo,
  StyledIntlRfpEventInfoForm,
  StyledDivider,
  StyledCtaContainer,
} from './IntlRfpEventInfo.styles';
import { RfpSessionData } from '../EventProfile/EventProfile.types';
import { IntlRfpFormData, IntlRfpEventInfoProps } from './IntlRfpEventInfo.types';
import { useSubmitRfp } from '../ContactInfo/ContactInfo.transformer';
import { RFP_SIEBEL_ERROR_CODE, RFP_SVC_SIEBEL, RFP_SVC_HENRY } from '../../constants';

export const IntlRfpEventInfoConfig = {
  emptyLabel: 'IntlRfpEventInfo',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/rfp/eventinfo`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const IntlRfpEventInfo: FC<{ model: IntlRfpEventInfoProps }> = (props: any) => {
  const {
    eventProfile,
    userInfo,
    userAddress,
    additionalInfo,
    submitCta,
    rfpConfirmationUrl,
    propertiesNotFoundError,
    siebelError,
    apiError,
  } = props.model;
  const { eventProfileLabel, eventType, flexibleDates, attachments } = eventProfile;

  const defaultFlexibleDatesOption = flexibleDates.options.filter((option: OptionType) => option.selected === true);

  const router = useRouter();

  const { locale } = useLocaleStore();
  const { requestId, api } = useHeaderFieldsStore();
  const { setEventInfoData, setRfpData, setRfpSessionData } = useRfpStore();
  const { properties, setPropertyIds } = useRfpTrayStore();

  const [loader, setLoader] = useState(true);
  const [loading, setLoading] = useState(false);
  const [flexibleDateOptions, setFlexibleDateOptions] = useState(flexibleDates.options);
  const [eventTypeOptions, setEventTypeOptions] = useState(eventType.options);
  const [isOtherEvent, setIsOtherEvent] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  const { customerInfo } = useCustomerInfo();

  const {
    control,
    getValues,
    setValue,
    resetField,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm<IntlRfpFormData>({
    defaultValues: {
      eventName: '',
      dates: '',
      startDate: '',
      endDate: '',
      flexibleDates: {
        label: defaultFlexibleDatesOption[0]?.label,
        code: defaultFlexibleDatesOption[0]?.code,
        selected: defaultFlexibleDatesOption[0]?.selected,
      },
      eventType: {
        label: '',
        code: '',
        selected: false,
      },
      otherEventType: '',
      guestRooms: '',
      attendees: '',
      currency: {
        label: '',
        code: '',
        selected: false,
      },
      budget: '',
      files: [],
      userInfo: {
        firstName: '',
        lastName: '',
        email: '',
        callingCode: {
          label: '',
          value: '',
          code: '',
          description: '',
          shortDescription: '',
        },
        phoneNumber: '',
        rewardId: '',
      },
      addressInfo: {
        addressType: AddressType.BUSINESS,
        companyName: '',
        addressLine1: '',
        addressLine2: '',
        country: {
          label: '',
          value: '',
          code: '',
          description: '',
        },
        city: '',
        state: '',
        zipcode: '',
      },
      additionalInfo: '',
    },
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    if (customerInfo?.userInfo) {
      setValue('userInfo.firstName', customerInfo.userInfo.firstName);
      setValue('userInfo.lastName', customerInfo.userInfo.lastName);
      setValue('userInfo.email', customerInfo.userInfo.email);
      setValue('userInfo.rewardId', customerInfo.userInfo.rewardId);
      setValue('userInfo.phoneNumber', customerInfo.userInfo.phoneNumber);
      customerInfo.userInfo.callingCode.description &&
        setValue('userInfo.callingCode', customerInfo.userInfo.callingCode);
    }
    customerInfo?.addressInfo && resetField('addressInfo', { defaultValue: customerInfo.addressInfo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerInfo]);

  useEffect(() => {
    fetchSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSession = async () => {
    try {
      const { sessionData } = (await getSession()) as RfpSessionData;
      const groupSearchData = sessionData?.cacheData?.data;

      const marshaCodesFromQueryparams = getQueryParams(window.location.search)?.['marshaCode'];
      const queryParamsPropertyIds = (marshaCodesFromQueryparams?.toUpperCase().split(',') || []).filter(
        id => id && !['__proto__', 'constructor', 'prototype'].includes(id.trim())
      );

      if (groupSearchData) {
        const ariesGroupSearch = groupSearchData.AriesGroupSearch;
        const propertyIds = ariesGroupSearch?.rfpShortlistedMarshaCodes
          ? Object.keys(ariesGroupSearch?.rfpShortlistedMarshaCodes)
          : [];
        const hasPropertyIdUpdates =
          queryParamsPropertyIds.length === 1 &&
          (propertyIds.length > 1 || (propertyIds.length === 1 && propertyIds[0] !== queryParamsPropertyIds[0]));

        if (!ariesGroupSearch || !propertyIds.length || hasPropertyIdUpdates) {
          setPropertyIds(queryParamsPropertyIds);
          // @ts-expect-error AriesGroupSearch requires all mandatory fields; only assigning rfpShortlistedMarshaCodes here.
          groupSearchData.AriesGroupSearch = {
            rfpShortlistedMarshaCodes: { [queryParamsPropertyIds[0]]: '' },
          };
        } else {
          setPropertyIds(propertyIds);
          prepopulateSearchCriteria(
            groupSearchData,
            flexibleDateOptions,
            eventTypeOptions,
            setFlexibleDateOptions,
            setEventTypeOptions,
            setValue
          );
        }

        setRfpSessionData(groupSearchData);
        updateDataLayerWithRFPAttributes(groupSearchData);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  const [submitRfp] = useSubmitRfp();

  const submitRfpCallback = (data: IntlRfpFormData) => {
    if (!properties || !properties.length) {
      setError(propertiesNotFoundError);
      window.scrollTo(0, 0);
      return;
    }

    setError(undefined);
    setLoading(true);

    const startDate = getFormattedCalendarDate(data.startDate, 'hyphenatedDateWithMonthNoAndYear');
    const endDate = getFormattedCalendarDate(data.endDate, 'hyphenatedDateWithMonthNoAndYear');
    data.startDate = startDate;
    data.endDate = endDate;

    setEventInfoData({ ...getValues(), startDate, endDate });

    const intlRfpInput = getSubmitIntlRfpInput(properties, data, locale);
    const extraPrefix = api === 'false' ? RFP_SVC_HENRY : RFP_SVC_SIEBEL;
    submitRfp({
      variables: { input: intlRfpInput },
      context: {
        headers: {
          'accept-language': locale,
          'x-request-id': createCustomXRequestId('rfp_intl', 'create', requestId, locale, extraPrefix),
          ...(api && { ecoServiceFlag: api }),
        },
      },
      fetchPolicy: 'no-cache',
      onCompleted: async response => {
        const rfpData = response.sales.groupSales.createRfp;
        const { rfpId, messages } = rfpData || {};
        if (rfpId) {
          setRfpData({ rfpId });
          await router.push(rfpConfirmationUrl);
        } else {
          setError(messages?.[0].user?.id === RFP_SIEBEL_ERROR_CODE ? siebelError : apiError);
          window.scrollTo(0, 0);
        }
        setLoading(false);
      },
      onError: () => {
        setError(apiError);
        setLoading(false);
        window.scrollTo(0, 0);
      },
    });
  };

  if (loader) {
    return <div className="m-spinner-m d-block mx-auto my-4" />;
  }

  return (
    <EditableComponent config={IntlRfpEventInfoConfig} {...props}>
      <CustomDomContentLoaded>
        <StyledIntlRfpEventInfo
          data-testid="eventinfo"
          data-component-name="o-groups-eventinfo"
          className="fullbleed m-container-fullbleed"
        >
          <div className="container pb-3 px-0">
            {error ? (
              <div className="mb-4">
                <InlineMessages type={InlineMessagesType.Error} title={error} severity="1" />
              </div>
            ) : null}
            <form onSubmit={handleSubmit(submitRfpCallback)}>
              <StyledIntlRfpEventInfoForm>
                <div className="t-subtitle-l event-info-title">{eventProfileLabel}</div>
                <EventInfo
                  labels={eventProfile}
                  flexibleDateOptions={flexibleDateOptions}
                  eventTypeOptions={eventTypeOptions}
                  isOtherEvent={isOtherEvent}
                  setIsOtherEvent={setIsOtherEvent}
                  showBudget={true}
                  control={control}
                  setValue={setValue}
                  getValues={getValues}
                  errors={errors}
                  clearErrors={clearErrors}
                  isIntlRfp={true}
                />
                <StyledDivider />
                <div className="t-subtitle-l pb-3">{attachments.label}</div>
                <Attachments labels={attachments} control={control} />
                <StyledDivider />
                <UserInfo
                  labels={userInfo}
                  customerCallingCountry={customerInfo?.userInfo.callingCode.shortDescription}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  clearErrors={clearErrors}
                />
                <StyledDivider className="user-address-info-divider" />
                <AddressInfo
                  labels={userAddress}
                  customerAddressType={customerInfo?.addressInfo.addressType}
                  customerCountry={customerInfo?.addressInfo.country.value}
                  customerState={customerInfo?.addressInfo.state}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                  clearErrors={clearErrors}
                />
                <StyledDivider />
                <AdditionalInfo labels={additionalInfo} control={control} />
              </StyledIntlRfpEventInfoForm>
              <StyledCtaContainer>
                <Button
                  type={Types.ButtonTypeVariation.Submit}
                  ariaLabel={submitCta.ariaLabel}
                  className={clsx('m-button-primary m-button-m submit-cta', loading && 'skeleton-loader')}
                  isDisabled={loading}
                >
                  {submitCta.label}
                </Button>
              </StyledCtaContainer>
            </form>
          </div>
        </StyledIntlRfpEventInfo>
      </CustomDomContentLoaded>
    </EditableComponent>
  );
};
